import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'
import {useQueryClient, type QueryClient} from '@github-ui/react-query'
import type {EstablishedAhpSession, EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import type {
  EstablishedAhpInitialSubmissionCoordinator,
  InitialAhpSubmissionRequest,
} from '@github-ui/ahp-session-client/initial-submission'
import {createEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'

import type {ChatTask, DebugEvent} from '../types'
import {persistedTaskTitleQueryKey} from '../providers/use-persisted-task-title'
import {CHAT_HISTORY_QUERY_KEY_PREFIX, CHAT_SIDEBAR_QUERY_KEY_PREFIX} from './chat-history'
import {
  createEnvironmentTaskEstablishedAhpSessionBootstrap,
  type MissionControlRuntimeConfig,
} from './mission-control-runtime'
import {createDebugEvent, createDebugWebSocketFactory} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'
import {useSessionHandoffCallbacks} from './use-session-handoff-callbacks'
import {
  beginTaskStartup,
  completeTaskStartup,
  failTaskStartup,
  getTaskStartup,
  subscribeTaskStartupStore,
  type TaskStartup,
} from '@github-ui/agent-sessions/ahp/task-startup-store'
import {getRelayProvisioning, subscribeRelayRegistry} from '@github-ui/ahp-relay/relay-registry'
import {EarlyTaskProvisioning} from '@github-ui/agent-sessions/ahp/early-task-provisioning'

export interface MissionControlInitialSubmission extends EstablishedAhpInitialSubmissionCoordinator {
  earlyProvisioning?: EarlyTaskProvisioning
}

/**
 * The minimum a client needs to provision an environment task on demand. `apiURL`
 * comes from the Rails-rendered payload; Mission Control returns the concrete
 * pre-bound task, environment, and session ids at provision time.
 *
 * `githubToken` is optional: when omitted (the real-UI default) the agent
 * sessions token endpoint (`POST /copilot/agent-sessions/token`) mints a
 * short-lived, refreshing CAPI token, so no token has to be rendered into the
 * page. A static token is only used for local development.
 */
export interface MissionControlRuntimeInput {
  apiURL: string
  githubToken?: string
  workingDirectory?: string
  /** Recreates the runtime when the owning chat scope changes. */
  instanceKey?: string
  onSessionLifecycle?: MissionControlRuntimeConfig['onSessionLifecycle']
}

/**
 * Build the Mission Control runtime for a new chat. It provisions an environment task
 * on submission (or deliberate interaction in the early-task experiment), creates the AHP
 * session, dispatches that prompt over relay, then observes the shared relay.
 *
 * Returns `undefined` until the payload carries an `apiURL` to provision against.
 */
export interface MissionControlRuntimeState {
  initialSubmission: MissionControlInitialSubmission | undefined
  initialSubmissionPending: boolean
  establishedAhpSession: EstablishedAhpSession | undefined
  taskStartupTaskId: string | undefined
  taskStartup: TaskStartup | undefined
  taskStartupCloneProgress: number | undefined
  onSessionEstablished: (session: EstablishedAhpSession) => void
  onSessionRenamed: (taskId: string, title: string) => void
  onUserRename: () => void
  reset: () => void
  debugEvents: readonly DebugEvent[]
}

export interface MissionControlRuntimeCallbacks {
  onTaskCreated?: (taskId: string) => void
  onSessionProvisioned?: (identity: EstablishedAhpSessionIdentity) => void
}

export class TaskStartupLifecycle {
  readonly runtimeKey: string | undefined
  private pendingPrompt: InitialAhpSubmissionRequest['prompt'] | undefined
  private taskId: string | undefined

  constructor(runtimeKey: string | undefined) {
    this.runtimeKey = runtimeKey
  }

  startSubmission(prompt: InitialAhpSubmissionRequest['prompt']): void {
    this.pendingPrompt = prompt
    if (this.taskId) beginTaskStartup(this.taskId, prompt, {kind: 'managed-sandbox'})
  }

  taskCreated(taskId: string): void {
    if (this.taskId && this.taskId !== taskId) completeTaskStartup(this.taskId)
    this.taskId = taskId
    if (this.pendingPrompt) beginTaskStartup(taskId, this.pendingPrompt, {kind: 'managed-sandbox'})
  }

  complete(taskId?: string): void {
    const activeTaskId = taskId ?? this.taskId
    if (activeTaskId) completeTaskStartup(activeTaskId)
    this.pendingPrompt = undefined
  }

  fail(error: unknown): void {
    if (this.taskId) failTaskStartup(this.taskId, error)
    this.pendingPrompt = undefined
  }

  reset(): void {
    if (this.taskId) completeTaskStartup(this.taskId)
    this.pendingPrompt = undefined
    this.taskId = undefined
  }
}

function useLatestCallback<T>(callback: ((value: T) => void) | undefined): (value: T) => void {
  const latestRef = useRef(callback)
  useEffect(() => {
    latestRef.current = callback
  }, [callback])
  return useCallback((value: T) => latestRef.current?.(value), [])
}

export interface SessionTitleLock {
  isLocked: () => boolean
  lock: () => void
  reset: () => void
}

export function useSessionTitleLock(): SessionTitleLock {
  const lockedRef = useRef(false)
  const isLocked = useCallback(() => lockedRef.current, [])
  const lock = useCallback(() => {
    lockedRef.current = true
  }, [])
  const reset = useCallback(() => {
    lockedRef.current = false
  }, [])
  return useMemo(() => ({isLocked, lock, reset}), [isLocked, lock, reset])
}

export async function applyRenamedTitleToCaches(
  queryClient: QueryClient,
  taskId: string,
  title: string,
  apiURL?: string,
): Promise<void> {
  const queryKey = persistedTaskTitleQueryKey(taskId, apiURL)
  await queryClient.cancelQueries({queryKey})
  queryClient.setQueryData<string>(queryKey, title)
  patchTaskTitleInLists(queryClient, taskId, title)
  await queryClient.invalidateQueries({queryKey, refetchType: 'none'})
}

// Optimistically patch every cached ChatTask list so the sidenav (and embedded
// history view) reflect the rename before the Alive channel's debounced
// invalidate lands.
function patchTaskTitleInLists(queryClient: QueryClient, taskId: string, title: string): void {
  const update = (previous: ChatTask[] | undefined): ChatTask[] | undefined => {
    if (!previous) return previous
    let changed = false
    const next = previous.map(task => {
      if (task.taskId !== taskId || task.title === title) return task
      changed = true
      return {...task, title}
    })
    return changed ? next : previous
  }
  queryClient.setQueriesData<ChatTask[]>({queryKey: [CHAT_SIDEBAR_QUERY_KEY_PREFIX]}, update)
  queryClient.setQueriesData<ChatTask[]>({queryKey: [CHAT_HISTORY_QUERY_KEY_PREFIX]}, update)
}

export function useMissionControlRuntime(
  input: MissionControlRuntimeInput | undefined,
  callbacks: MissionControlRuntimeCallbacks = {},
): MissionControlRuntimeState {
  'use no memo'
  // The runtime key is an identity boundary even though it is not runtime configuration.
  const apiURL = input?.apiURL
  const githubToken = input?.githubToken
  const workingDirectory = input?.workingDirectory
  const instanceKey = input?.instanceKey
  const onSessionLifecycle = input?.onSessionLifecycle
  const {navigateToSession, confirmCreateSession} = useSessionHandoffCallbacks()
  const sessionTitleLock = useSessionTitleLock()
  const onSessionProvisioned = useLatestCallback(callbacks.onSessionProvisioned)
  const onTaskCreated = useLatestCallback(callbacks.onTaskCreated)
  const runtimeKey = apiURL
    ? [apiURL, githubToken ?? '', workingDirectory ?? '', instanceKey ?? ''].join('\0')
    : undefined
  const startupLifecycle = useMemo(() => new TaskStartupLifecycle(runtimeKey), [runtimeKey])
  const earlyProvisioning = useMemo(
    () => (apiURL && runtimeKey ? new EarlyTaskProvisioning(apiURL, githubToken) : undefined),
    [apiURL, githubToken, runtimeKey],
  )
  const [taskStartupTaskId, setTaskStartupTaskId] = useState<string>()
  const [initialSubmissionPending, setInitialSubmissionPending] = useState(false)
  const taskStartup = useSyncExternalStore(
    subscribeTaskStartupStore,
    () => getTaskStartup(taskStartupTaskId),
    () => undefined,
  )
  const relayProvisioning = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayProvisioning(taskStartupTaskId),
    () => undefined,
  )
  const reportedCloneProgress = relayProvisioning?.phase === 'cloning' ? relayProvisioning.progress : undefined
  const taskStartupCloneProgress =
    reportedCloneProgress !== undefined && Number.isFinite(reportedCloneProgress)
      ? Math.min(100, Math.max(0, Math.round(reportedCloneProgress)))
      : undefined
  const handleTaskCreated = useCallback(
    (taskId: string) => {
      startupLifecycle.taskCreated(taskId)
      setTaskStartupTaskId(taskId)
      onTaskCreated(taskId)
    },
    [onTaskCreated, startupLifecycle],
  )
  const queryClient = useQueryClient()
  const onSessionRenamed = useCallback(
    (taskId: string, title: string) => applyRenamedTitleToCaches(queryClient, taskId, title, apiURL),
    [apiURL, queryClient],
  )
  const establishedBootstrap = useMemo(
    () =>
      apiURL && runtimeKey
        ? createEnvironmentTaskEstablishedAhpSessionBootstrap({
            apiURL,
            githubToken,
            workingDirectory,
            navigateToSession,
            confirmCreateSession,
            onSessionRenamed,
            isSessionTitleLocked: sessionTitleLock.isLocked,
            onTaskCreated: handleTaskCreated,
            onSessionLifecycle,
            earlyProvisioning,
          })
        : undefined,
    [
      apiURL,
      githubToken,
      runtimeKey,
      workingDirectory,
      navigateToSession,
      confirmCreateSession,
      onSessionRenamed,
      sessionTitleLock.isLocked,
      handleTaskCreated,
      onSessionLifecycle,
      earlyProvisioning,
    ],
  )
  const [establishedAhpSession, setEstablishedAhpSession] = useState<EstablishedAhpSession>()
  const [debugEvents, setDebugEvents] = useState<DebugEvent[]>([])
  const appendDebugEvent = useCallback((event: Omit<DebugEvent, 'id' | 'timestamp'>) => {
    setDebugEvents(previous => [...previous, createDebugEvent(event)])
  }, [])
  const initialSubmission = useMemo(() => {
    if (!apiURL || !runtimeKey || !establishedBootstrap) {
      return undefined
    }
    const coordinator = createEstablishedAhpInitialSubmissionCoordinator({
      bootstrap: establishedBootstrap,
      onIdentityProvisioned: onSessionProvisioned,
    })
    return {
      earlyProvisioning,
      prepare: coordinator.prepare?.bind(coordinator),
      cancel: coordinator.cancel?.bind(coordinator),
      dispose: coordinator.dispose?.bind(coordinator),
      submit: async (request: InitialAhpSubmissionRequest) => {
        earlyProvisioning?.onBeforeSubmit()
        startupLifecycle.startSubmission(request.prompt)
        setInitialSubmissionPending(true)
        try {
          const session = await coordinator.submit(request)
          startupLifecycle.complete(session.identity.taskId)
          return session
        } catch (error) {
          startupLifecycle.fail(error)
          throw error
        } finally {
          setInitialSubmissionPending(false)
        }
      },
    }
  }, [apiURL, runtimeKey, establishedBootstrap, onSessionProvisioned, startupLifecycle, earlyProvisioning])

  useEffect(() => {
    if (!establishedBootstrap) return
    establishedBootstrap.configureRelayDiagnostics?.({
      webSocketFactory: createDebugWebSocketFactory(appendDebugEvent),
      onStatus: (status, error) => {
        appendDebugEvent({
          source: 'relay',
          direction: 'status',
          message: error ? `${status}: ${error}` : status,
        })
      },
    })
  }, [appendDebugEvent, establishedBootstrap])

  useEffect(() => {
    return () => {
      establishedBootstrap?.reset()
      startupLifecycle.reset()
      sessionTitleLock.reset()
      setEstablishedAhpSession(undefined)
    }
  }, [establishedBootstrap, sessionTitleLock, startupLifecycle])

  const reset = useCallback(() => {
    establishedBootstrap?.reset()
    startupLifecycle.reset()
    setTaskStartupTaskId(undefined)
    setInitialSubmissionPending(false)
    sessionTitleLock.reset()
    setEstablishedAhpSession(undefined)
    setDebugEvents([])
  }, [establishedBootstrap, sessionTitleLock, startupLifecycle])
  const onSessionEstablished = useCallback((session: EstablishedAhpSession) => {
    setEstablishedAhpSession(session)
    setTaskStartupTaskId(undefined)
  }, [])

  return {
    initialSubmission,
    initialSubmissionPending,
    establishedAhpSession,
    taskStartupTaskId,
    taskStartup,
    taskStartupCloneProgress,
    onSessionEstablished,
    onSessionRenamed,
    onUserRename: sessionTitleLock.lock,
    reset,
    debugEvents,
  }
}
